import { useRef, useState, type FormEvent } from "react";
import { formatRuPhone, isRuPhoneComplete } from "../phone";
import { api, ApiError, type Bench, type PublicOrder } from "../api";
import type { useCart } from "../state/cart";
import { exclusionLabel, money, Photo } from "../ui";

type Cart = ReturnType<typeof useCart>;

/**
 * Корзина и оформление одним листом.
 *
 * Гость сидит на лавочке и хочет поесть, а не ходить по шагам, поэтому состав,
 * промокод, контакты и согласия помещаются на один экран с прокруткой.
 */
export function CartSheet({
  cart,
  bench,
  benchRef,
  minOrderAmount,
  canOrder,
  promoCode,
  onPromoCode,
  onClose
}: {
  cart: Cart;
  bench: Bench | null;
  benchRef: string;
  minOrderAmount: number;
  canOrder: boolean;
  promoCode: string;
  onPromoCode: (value: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [comment, setComment] = useState("");
  const [delivery, setDelivery] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Ожидание затянулось.
   *
   * Оформление заказа с лавочки ждёт подтверждения фудтрака — там кто-то
   * должен взять заказ в работу, прежде чем гостю скажут «принято». Обычно
   * это доли секунды, но на мобильной связи бывает и дольше, и молчащая
   * кнопка на десятой секунде выглядит как зависшая.
   */
  const [waiting, setWaiting] = useState(false);

  // Ключ живёт столько же, сколько открытая форма оформления: повторное
  // нажатие «Оплатить» — это та же попытка, а не вторая (п. 30 ТЗ).
  const attemptKey = useRef(api.newIdempotencyKey());

  const { totals } = cart;
  const gifts = totals.lines.filter(line => line.gift);
  const shortfall = Math.max(0, minOrderAmount - totals.subtotal);
  const saved = totals.promotionDiscount + totals.discount;

  // Имя нужно доставщику: заказ несут к лавочке, а на лавочке может сидеть
  // компания, где каждый заказал сам за себя.
  const phoneReady = isRuPhoneComplete(phone);
  const ready =
    canOrder && cart.count > 0 && shortfall === 0 && delivery && privacy && name.trim() !== "" && phoneReady && !busy;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    const explain = setTimeout(() => setWaiting(true), 2500);

    try {
      const order = await api.once<PublicOrder & { id: string; token: string }>(
        "/api/orders",
        {
          benchId: benchRef,
          customerName: name.trim(),
          customerPhone: phone.trim(),
          comment: comment.trim() || undefined,
          promoCode: promoCode.trim() || undefined,
          marketingOptIn: marketing,
          deliveryAccepted: delivery,
          privacyAccepted: privacy,
          items: cart.items.map(item => ({
            productId: item.productId,
            qty: item.qty,
            modifiers: item.modifiers.map(modifier => ({
              groupId: modifier.groupId,
              optionId: modifier.optionId,
              qty: modifier.qty
            }))
          }))
        },
        attemptKey.current
      );

      /*
       * Оплата идёт на стороне банка: если эквайринг настроен, сервер отвечает
       * адресом платёжной страницы, и гость уходит туда. Пока доступов к банку
       * нет, сервер проводит заказ тестовым адаптером и возвращает его сам.
       */
      const payment = await api.once<{ formUrl?: string | null; order?: { token?: string } }>(
        `/api/orders/${order.id}/pay`,
        {},
        attemptKey.current
      );

      cart.clear();

      if (payment.formUrl) {
        window.location.href = payment.formUrl;
        return;
      }

      window.location.href = `/order/${payment.order?.token ?? order.token}`;
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Не удалось оформить заказ. Попробуйте ещё раз.");
      setBusy(false);
    } finally {
      clearTimeout(explain);
      setWaiting(false);
    }
  }

  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Корзина"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form className="sheet" onSubmit={submit}>
        <div className="sheet-head">
          <h2>Корзина</h2>
          {bench ? <small>Принесём к лавочке №{bench.label}</small> : null}
        </div>

        {cart.items.map((item, index) => {
          const line = totals.lines[index];

          return (
            <div className="cart-line" key={`${item.productId}-${index}`}>
              <span className="cart-thumb">
                <Photo src={item.imageUrl} title={item.title} />
              </span>

              <span>
                <b>{item.title}</b>
                {item.modifiers.length > 0 ? (
                  <small>
                    {item.modifiers
                      .map(modifier =>
                        modifier.exclusion
                          ? exclusionLabel(modifier.title)
                          : `+ ${modifier.title}${modifier.qty > 1 ? ` ×${modifier.qty}` : ""}`
                      )
                      .join(" · ")}
                  </small>
                ) : null}
                {line?.promotions.map(promotion => (
                  <span className="promo-tag" key={promotion.id}>
                    {promotion.title}
                  </span>
                ))}

                <span className="stepper" style={{ background: "var(--surface)", marginTop: 6 }}>
                  <button type="button" aria-label="Меньше" onClick={() => cart.setQty(index, item.qty - 1)}>
                    −
                  </button>
                  <b>{item.qty}</b>
                  <button
                    type="button"
                    aria-label="Больше"
                    disabled={item.qty >= 20}
                    onClick={() => cart.setQty(index, item.qty + 1)}
                  >
                    +
                  </button>
                </span>
              </span>

              <span className="sum">
                {line && line.discount ? (
                  <>
                    <span className="old-price">{money(line.baseTotal)}</span>
                    <br />
                  </>
                ) : null}
                {money(line ? line.total : 0)}
              </span>
            </div>
          );
        })}

        {gifts.map((gift, index) => (
          <div className="cart-line" key={`gift-${index}`}>
            <span className="cart-thumb">
              <Photo src={null} title={gift.title} />
            </span>
            <span>
              <b>{gift.title}</b>
              <span className="gift-tag">Подарок</span>
            </span>
            <span className="sum">{money(0)}</span>
          </div>
        ))}

        <label className="field">
          <span>Промокод</span>
          <input
            value={promoCode}
            onChange={event => onPromoCode(event.target.value.toUpperCase())}
            placeholder="Если есть"
            autoComplete="off"
          />
        </label>

        {totals.promoError ? <p className="alert warn">{totals.promoError.message}</p> : null}
        {totals.promo && !totals.promoError ? (
          <p className="alert good">Промокод {totals.promo} применён</p>
        ) : null}

        <div className="totals">
          <div className="row-muted">
            <span>Товары</span>
            <span>{money(totals.baseSubtotal)}</span>
          </div>
          {totals.promotionDiscount ? (
            <div className="row-save">
              <span>Акции</span>
              <span>−{money(totals.promotionDiscount)}</span>
            </div>
          ) : null}
          {totals.discount ? (
            <div className="row-save">
              <span>Промокод</span>
              <span>−{money(totals.discount)}</span>
            </div>
          ) : null}
          <div className="row-muted">
            <span>Доставка к лавочке</span>
            <span>бесплатно</span>
          </div>
          <div className="row-grand">
            <span>Итого</span>
            <span>{money(totals.total)}</span>
          </div>
          {saved > 0 ? (
            <div className="row-save">
              <span>Вы экономите</span>
              <span>{money(saved)}</span>
            </div>
          ) : null}
        </div>

        {shortfall > 0 ? (
          <p className="alert warn">До минимального заказа не хватает {money(shortfall)}</p>
        ) : null}

        <label className="field">
          <span>Имя *</span>
          <input
            value={name}
            onChange={event => setName(event.target.value)}
            autoComplete="name"
            placeholder="Как к вам обращаться"
            required
          />
          <small className="hint">Курьер будет искать вас по имени — на лавочке может сидеть не только ваша компания.</small>
        </label>

        <label className="field">
          <span>Телефон *</span>
          <input
            value={phone}
            /*
             * Номер приводится к российскому прямо во время набора: код страны
             * подставляется сам, восьмёрка становится семёркой, лишние цифры
             * отбрасываются. Подсказать об ошибке после отправки было бы поздно —
             * гость уже ушёл бы с формы.
             */
            onChange={event => setPhone(formatRuPhone(event.target.value))}
            inputMode="tel"
            autoComplete="tel"
            placeholder="+7 900 000-00-00"
            required
          />
          {phone && !phoneReady ? (
            <small className="hint">Номер неполный — нужно 11 цифр вместе с семёркой.</small>
          ) : (
            <small className="hint">По нему доставщик позвонит, если не найдёт вас на лавочке.</small>
          )}
        </label>

        <label className="field">
          <span>Комментарий</span>
          <textarea
            value={comment}
            onChange={event => setComment(event.target.value)}
            placeholder="Например: сидим у фонтана, с коляской"
          />
        </label>

        <label className="check">
          <input type="checkbox" checked={delivery} onChange={event => setDelivery(event.target.checked)} />
          <span>
            Я понимаю, что заказ принесут <b>только к лавочке №{bench?.label ?? "—"}</b>, указанной в заказе,
            и только в пределах зоны работы фудтрака.
          </span>
        </label>

        <label className="check">
          <input type="checkbox" checked={privacy} onChange={event => setPrivacy(event.target.checked)} />
          <span>Согласен с обработкой персональных данных</span>
        </label>

        <label className="check">
          <input type="checkbox" checked={marketing} onChange={event => setMarketing(event.target.checked)} />
          <span>Хочу получать сообщения об акциях (необязательно)</span>
        </label>

        {error ? <p className="alert bad">{error}</p> : null}

        <button className="wide-btn" type="submit" disabled={!ready}>
          {busy
            ? waiting
              ? "Ждём фудтрак…"
              : "Оформляем…"
            : `Оплатить ${money(totals.total)}`}
        </button>

        <button className="wide-btn ghost" type="button" onClick={onClose}>
          Вернуться к меню
        </button>
      </form>
    </div>
  );
}
